import { redirectToSessionExpired } from '@src/lib/session-redirect';
import { toastNotificationSwitch } from '@src/lib/toast-notification-switch';
import {
  DefaultOptions,
  MutationCache,
  QueryCache,
  QueryClient,
  UseMutationOptions,
} from '@tanstack/react-query';
import { ToastUtility } from '@utils';
import { AxiosError, isAxiosError } from 'axios';

const showErrorNotification = (errorMessage: string) =>
  ToastUtility.error(`${errorMessage}. Check console for more details.`, {
    toastId: errorMessage,
  });

const handleAxiosError = (error: AxiosError, errorMessage?: string) => {
  if (error.status === 401) {
    redirectToSessionExpired();
  } else {
    const message = errorMessage ? errorMessage : error.message;
    showErrorNotification(message);
  }
};

const handleResponseError = (error: Response, errorMessage?: string) => {
  if (error.status === 401) {
    redirectToSessionExpired();
  } else {
    const message = errorMessage
      ? errorMessage
      : `${error.statusText} + error. Check console for more information.`;
    showErrorNotification(message);
  }
};

const handleUnknownError = (errorMessage?: string) => {
  const message = errorMessage ? errorMessage : 'An error has occurred';
  showErrorNotification(message);
};

export const handleApiError = (error: unknown, errorMessage?: string) => {
  console.error(error);
  if (isAxiosError(error)) {
    handleAxiosError(error, errorMessage);
  } else if (error instanceof Response) {
    handleResponseError(error, errorMessage);
  } else {
    handleUnknownError(errorMessage);
  }
};

export const queryConfig: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60 * 10, // 10 minutes
    throwOnError: (error) => {
      if (isAxiosError(error)) {
        // Only throw server errors (500+)
        return (
          error.response?.status !== undefined && error.response.status >= 500
        );
      }
      return error instanceof Response ? error.status >= 500 : true;
    },
  },
} satisfies DefaultOptions;

const checkFor401 = (error: unknown) => {
  const is401 =
    (isAxiosError(error) && error.response?.status === 401) ||
    (error instanceof Response && error.status === 401);
  if (is401) {
    redirectToSessionExpired();
  }
};

export const queryCacheConfig = new QueryCache({
  onError: (error: unknown, query) => {
    checkFor401(error);
    toastNotificationSwitch(query.queryKey[0] as string);
  },
});

export const mutationCacheConfig = new MutationCache({
  onError: (error: unknown) => checkFor401(error),
});

export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
  queryCache: queryCacheConfig,
  mutationCache: mutationCacheConfig,
});

export type ApiFnReturnType<
  FnType extends (...args: unknown[]) => Promise<unknown>,
> = Awaited<ReturnType<FnType>>;

// This type now checks if T is a function.
// If so, it gets the ReturnType. If not, it just uses T itself.
// This makes it compatible with both patterns in your codebase.
export type QueryConfig<T> = Omit<
  T extends (...args: unknown[]) => unknown ? ReturnType<T> : T,
  'queryKey' | 'queryFn'
>;

export type MutationConfig<
  MutationFnType extends (...args: unknown[]) => Promise<unknown>,
> = UseMutationOptions<
  ApiFnReturnType<MutationFnType>,
  Error,
  Parameters<MutationFnType>[0]
>;
